/**
 * The parts every panel form shares: a column of fields, one primary submit
 * beside Cancel, the leave guard, and the hand-over that closes the form and
 * opens the record it made. A form composes these and its own fields.
 */
import { createContext, useContext, useEffect, type FormEvent, type ReactNode } from 'react';

import { Button } from '../../ui/Button.js';
import { useUnsavedChanges } from '../../lib/unsaved.js';
import { type RecordRef, useRecordPanel } from './record.js';

/**
 * How the open form tells the panel it has changes, and how anything that
 * closes the form (its Cancel, the header's, Escape) asks first when it does.
 * The panel provides it around the form; one confirm, one code path.
 */
export interface PanelFormGuard {
  dirty: { current: boolean };
  requestClose: () => void;
}

export const PanelFormGuardContext = createContext<PanelFormGuard>({
  dirty: { current: false },
  requestClose: () => {},
});

export const DISCARD_FORM = {
  title: 'Discard this form?',
  description: 'What you typed here will be lost.',
  confirmLabel: 'Discard',
  tone: 'danger' as const,
};

export function PanelFormFields({
  onSubmit,
  children,
}: {
  onSubmit: () => void;
  children: ReactNode;
}) {
  function handle(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }
  return (
    <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </form>
  );
}

/** One primary submit; Cancel asks first when there are changes. */
export function PanelFormActions({
  submitLabel,
  pending,
  disabled,
  dirty,
}: {
  submitLabel: string;
  pending: boolean;
  disabled?: boolean | undefined;
  dirty: boolean;
}) {
  const guard = useContext(PanelFormGuardContext);
  useUnsavedChanges(dirty);
  useEffect(() => {
    guard.dirty.current = dirty;
    return () => {
      guard.dirty.current = false;
    };
  }, [dirty, guard]);
  return (
    <div
      style={{
        display: 'flex',
        gap: 7,
        paddingTop: 11,
        borderTop: '1px solid var(--border-hairline)',
      }}
    >
      <Button
        type="submit"
        variant="primary"
        disabled={pending || disabled === true}
        style={{ flex: 1, justifyContent: 'center' }}
      >
        {pending ? `${submitLabel}…` : submitLabel}
      </Button>
      <Button type="button" variant="ghost" onClick={guard.requestClose}>
        Cancel
      </Button>
    </div>
  );
}

/**
 * What a form does when its work exists: close itself and, when the work is
 * a record, open that record in the same panel.
 */
export function usePanelFormDone(): (made?: RecordRef) => void {
  const panel = useRecordPanel();
  return (made) => {
    if (made) panel.open(made);
    else panel.closeForm();
  };
}
